import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '@/admin/lib/api'

// ── Types ────────────────────────────────────────────────

export interface AssessmentLineItem {
  uuid: string
  description: string
  amount: number
  notes: string
  order: number
  is_locked: boolean
  /** Child claim this row assesses (one row per item). Null for hand-added rows. */
  source_child_claim: string | null
  /** The child's live workflow state - what the row reflects (child = source of truth). */
  child_state?: string | null
  child_state_key?: string | null
  /** Child in a terminal state (decided / rejected) - already assessed. */
  child_resolved?: boolean
  created_at: string
  updated_at: string
  /** Per-row EAV (coverage_decision, ...), flattened by the serializer. */
  coverage_decision?: string | null
  [key: string]: unknown
}

export interface BulkDecideResult {
  summary: { decided: string[]; skipped: string[]; errors: { line: string; detail: string }[] }
  line_items: AssessmentLineItem[]
}

export interface AssessmentOptionItem {
  value: string
  label: string
  /** Extra per-option metadata (e.g. badge variant). */
  [key: string]: unknown
}

export interface AssessmentOptions {
  coverage_decisions: AssessmentOptionItem[]
}

// ── Queries ──────────────────────────────────────────────

export function useAssessmentLineItems(claimUuid: string, enabled = true) {
  return useQuery<AssessmentLineItem[]>({
    queryKey: ['assessment-line-items', claimUuid],
    queryFn: () =>
      api
        .get(`/claims/claims/${claimUuid}/assessment/line-items/`)
        .then((r) => r.data?.data ?? r.data),
    enabled: enabled && !!claimUuid,
  })
}

export function useAssessmentOptions(enabled = true) {
  return useQuery<AssessmentOptions>({
    queryKey: ['assessment-options'],
    queryFn: () =>
      api
        .get('/claims/assessment/options/')
        .then((r) => r.data?.data ?? r.data),
    staleTime: 5 * 60 * 1000, // config-time data
    enabled,
  })
}

// ── Mutations ────────────────────────────────────────────

function invalidate(qc: ReturnType<typeof useQueryClient>, claimUuid: string) {
  qc.invalidateQueries({ queryKey: ['assessment-line-items', claimUuid] })
  qc.invalidateQueries({ queryKey: ['claim', claimUuid] })
}

export function useCreateAssessmentLineItem(claimUuid: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api
        .post(`/claims/claims/${claimUuid}/assessment/line-items/`, data)
        .then((r) => r.data),
    onSuccess: () => invalidate(qc, claimUuid),
  })
}

export function useUpdateAssessmentLineItem(claimUuid: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      itemUuid,
      data,
    }: {
      itemUuid: string
      data: Record<string, unknown>
    }) =>
      api
        .put(
          `/claims/claims/${claimUuid}/assessment/line-items/${itemUuid}/`,
          data,
        )
        .then((r) => r.data),
    onSuccess: () => invalidate(qc, claimUuid),
  })
}

export function useDeleteAssessmentLineItem(claimUuid: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (itemUuid: string) =>
      api.delete(
        `/claims/claims/${claimUuid}/assessment/line-items/${itemUuid}/`,
      ),
    onSuccess: () => invalidate(qc, claimUuid),
  })
}

export function useReorderAssessmentLineItems(claimUuid: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (orderedUuids: string[]) =>
      api
        .post(`/claims/claims/${claimUuid}/assessment/line-items/reorder/`, {
          ordered_uuids: orderedUuids,
        })
        .then((r) => r.data),
    onSuccess: () => invalidate(qc, claimUuid),
  })
}

/** Commit one row: advance its child claim (covered -> decided, not-covered -> rejected). */
export function useDecideAssessmentLineItem(claimUuid: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (itemUuid: string) =>
      api
        .post(
          `/claims/claims/${claimUuid}/assessment/line-items/${itemUuid}/decide/`,
        )
        .then((r) => r.data?.data ?? r.data),
    onSuccess: () => invalidate(qc, claimUuid),
  })
}

/** Bulk-commit rows: decide all, or the given subset. Skips undecided rows. */
export function useBulkDecideAssessment(claimUuid: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (lineUuids?: string[]) =>
      api
        .post(`/claims/claims/${claimUuid}/assessment/decide/`, {
          line_uuids: lineUuids,
        })
        .then((r) => (r.data?.data ?? r.data) as BulkDecideResult),
    onSuccess: () => invalidate(qc, claimUuid),
  })
}
